import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';

import { replaceIds, ROUTES } from '../../config/routes/config';
import type { ProposalInfluencerStatus } from '../../types/proposal';
import { Tab } from '../campaigns/SingleCampaignTabs';
import { UsersImages } from '../images/UserImages';

type ProposalInfluencerTab = {
  id: string;
  name: string | null;
  profile_picture: string | null;
  status?: ProposalInfluencerStatus;
};

type SingleProposalTabsProps = {
  proposalId: string;
  influencers: ProposalInfluencerTab[];
};

export const SingleProposalTabs = ({
  proposalId,
  influencers,
}: SingleProposalTabsProps) => {
  const { t } = useTranslation('common');
  const location = useLocation();

  const isInfluencerSettingsTab = useMemo(
    () => /^\/proposal\/.*\/influencers/.test(location.pathname),
    [location],
  );

  const getUrl = (url: string) => replaceIds({ url, params: [{ proposalId }] });

  const getInfluencerUrl = (influencerId: string) =>
    replaceIds({
      url: ROUTES.SINGLE_PROPOSAL_INFLUENCER_SETTINGS,
      params: [{ proposalId }, { influencerId }],
    });

  const sortedInfluencers = useMemo(() => {
    // Separate into two groups: active/accepted vs rejected
    const active = influencers.filter((inf) => inf.status !== 'rejected');
    const rejected = influencers.filter((inf) => inf.status === 'rejected');

    // Sort each group alphabetically
    const sortedActive = active.sort((a, b) =>
      (a.name ?? '').localeCompare(b.name ?? ''),
    );
    const sortedRejected = rejected.sort((a, b) =>
      (a.name ?? '').localeCompare(b.name ?? ''),
    );

    // Combine: active first, then rejected
    return [...sortedActive, ...sortedRejected];
  }, [influencers]);

  // Tab pill navigates to first influencer by default
  const influencerSettingsTo =
    sortedInfluencers.length > 0
      ? getInfluencerUrl(sortedInfluencers[0].id)
      : getUrl(ROUTES.SINGLE_PROPOSAL_INFLUENCER_SETTINGS);

  return (
    <div className="flex flex-col gap-y-3">
      <div
        id="singleProposalTabs"
        className="flex flex-row gap-x-2 overflow-x-auto customScroll pb-1"
      >
        <Tab
          id="proposalGeneralTab"
          to={getUrl(ROUTES.SINGLE_PROPOSAL_GENERAL)}
          matchUrl={ROUTES.SINGLE_PROPOSAL_GENERAL}
          text={t('proposals.tabs.general')}
        />
        <Tab
          id="proposalInfluencerSettingsTab"
          to={influencerSettingsTo}
          matchUrl={ROUTES.SINGLE_PROPOSAL_INFLUENCER_SETTINGS}
          text={t('proposals.tabs.influencerSettings')}
        />
        <Tab
          id="proposalCampaignSettingsTab"
          to={getUrl(ROUTES.SINGLE_PROPOSAL_CAMPAIGN_SETTINGS)}
          matchUrl={ROUTES.SINGLE_PROPOSAL_CAMPAIGN_SETTINGS}
          text={t('proposals.tabs.campaignSettings')}
        />
      </div>

      {isInfluencerSettingsTab && sortedInfluencers.length > 0 && (
        <div
          id="proposalInfluencersTabs"
          className="flex flex-row gap-x-2 overflow-x-auto customScroll pb-1"
        >
          {sortedInfluencers.map((inf) => {
            const isRejected = inf.status === 'rejected';
            return (
              <NavLink
                key={inf.id}
                to={getInfluencerUrl(inf.id)}
                className={({ isActive }) =>
                  `${isActive ? '!bg-influentia-light-blue' : 'bg-white'} ${isRejected ? 'opacity-50 line-through decoration-1' : ''} flex-none text-sm items-center font-semibold text-influentia-black flex flex-row gap-x-2 rounded-full px-4 py-1`
                }
              >
                <UsersImages
                  userCount={1}
                  userImages={[inf.profile_picture || '']}
                  size="size-[24px]"
                />
                {inf.name || inf.id}
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
};
